<template>
  <div class="fair-pay-calculator-card">
    <!-- Not Available Mode -->
    <div
      v-if="calculator === 'not-available'"
      class="not-available-alert"
      role="alert"
    >
      <div class="alert-content">
        <div class="alert-text">
          <div class="alert-heading">Fair Pay calculation is not available</div>
          <div class="alert-description">
            Fair Pay calculation is available in the Readiness module. It uses
            current employee readiness to calculate fair pay in range. To make
            it available your company needs to subscribe to the Readiness
            module.
          </div>
        </div>
        <div class="alert-actions">
          <button
            class="btn-learn-more"
            @click="$emit('learn-more')"
            aria-label="Learn more about Fair Pay calculation"
          >
            Learn More
          </button>
        </div>
      </div>
    </div>

    <!-- All Other Modes -->
    <template v-else>
      <!-- Title Row -->
      <div class="title-row">
        <span class="title-text">{{
          calculator === 'read-only'
            ? 'Pay Range as of'
            : 'Fair Pay calculator at effective date'
        }}</span>
        <span class="title-date">{{ asOfDate }}</span>
      </div>

      <!-- Position Summary -->
      <div class="position-summary">
        <div class="position-row">
          <span class="position-label">Employee primary position at effective date</span>
        </div>
        <div class="position-detail">
          <PositionIcon size="small" class="position-icon" aria-hidden="true" />
          <span class="position-code">{{ position.code }}</span>
          <span v-if="position.name" class="position-name">{{ position.name }}</span>
          <span class="position-fte">{{ positionFteLabel }}</span>
        </div>
      </div>

      <!-- Pay Range Summary - 1 FTE -->
      <div class="pay-range-summary">
        <div class="pay-range-header-row">
          <span class="pay-range-label">{{
            fteAdjustmentApplied
              ? 'Pay range at effective date (1 FTE)'
              : 'Pay range at effective date'
          }}</span>
          <div class="pay-range-meta">
            <span v-if="payRangeVersion.code" class="pay-range-code">{{
              payRangeVersion.code
            }}</span>
            <span class="pay-range-version">V{{ payRangeVersion.version }}</span>
            <span class="pay-range-date">as of {{ payRangeVersion.asOfDate }}</span>
          </div>
        </div>
        <div class="pay-range-detail">
          <PayRangeHeader
            :country-flag="payRangeVersion.countryFlag"
            :pay-range-name="payRangeHeaderName"
            :pay-range-location="payRangeVersion.location"
            :pay-range-expressed-as="payRangeVersion.payBasis"
            :pay-range-currency-code="payRangeVersion.currency"
            :hover-enabled="false"
          />
          <div class="anchors-row">
            <span class="anchor-label">@ Readiness 0% -</span>
            <span class="anchor-value">{{ formatAnchor(payRangeVersion.anchors[0]) }}</span>
            <span class="anchor-label">, 50% -</span>
            <span class="anchor-value">{{ formatAnchor(payRangeVersion.anchors[50]) }}</span>
            <span class="anchor-label">, 100% -</span>
            <span class="anchor-value">{{ formatAnchor(payRangeVersion.anchors[100]) }}</span>
            <span class="anchor-label">, 150% -</span>
            <span class="anchor-value">{{ formatAnchor(payRangeVersion.anchors[150]) }}</span>
          </div>
        </div>
      </div>

      <!-- Pay Range Summary - Adjusted FTE -->
      <div v-if="fteAdjustmentApplied && payRangeAdjusted" class="pay-range-summary adjusted">
        <div class="pay-range-header-row">
          <span class="pay-range-label-adjusted">{{ adjustedLabel }}</span>
        </div>
        <div class="anchors-row">
          <span class="anchor-label">@ Readiness 0% -</span>
          <span class="anchor-value">{{ formatAnchor(payRangeAdjusted.anchors[0]) }}</span>
          <span class="anchor-label">, 50% -</span>
          <span class="anchor-value">{{ formatAnchor(payRangeAdjusted.anchors[50]) }}</span>
          <span class="anchor-label">, 100% -</span>
          <span class="anchor-value">{{ formatAnchor(payRangeAdjusted.anchors[100]) }}</span>
          <span class="anchor-label">, 150% -</span>
          <span class="anchor-value">{{ formatAnchor(payRangeAdjusted.anchors[150]) }}</span>
        </div>
      </div>

      <!-- Current Readiness Row -->
      <div
        v-if="calculator === 'current-and-simulated' && currentReadiness !== null"
        class="current-readiness-row"
      >
        <div class="readiness-info">
          <ReadinessCircle :value="currentReadiness" :size="22" />
          <span class="readiness-label">Current readiness</span>
          <span class="readiness-value">{{ currentReadiness }}%</span>
        </div>
        <div class="fair-pay-info" v-if="fairAtCurrent">
          <span class="fair-pay-label">Fair at current readiness</span>
          <span class="fair-pay-amount">{{ formatFairPay(fairAtCurrent) }}</span>
          <span class="fair-pay-variance" v-if="fairAtCurrent.variance">{{
            fairAtCurrent.variance
          }}</span>
        </div>
      </div>

      <!-- Simulate Readiness Row -->
      <div
        v-if="
          calculator === 'current-and-simulated' || calculator === 'simulated-only'
        "
        class="simulate-readiness-row"
      >
        <div class="simulate-label-wrapper">
          <span class="simulate-label">Simulate Readiness</span>
        </div>
        <div class="slider-wrapper">
          <ReadinessSlider
            :readiness="simulatedReadiness || 0"
            @update:modelValue="handleSliderUpdate"
          />
        </div>
        <div class="simulate-info">
          <span class="fair-pay-label">Fair at {{ simulatedReadiness || 0 }}% readiness</span>
          <span v-if="fairAtSimulated" class="fair-pay-amount">{{
            formatFairPay(fairAtSimulated)
          }}</span>
          <span
            v-if="fairAtSimulated && fairAtSimulated.variance"
            class="fair-pay-variance"
          >{{ fairAtSimulated.variance }}</span>
          <button
            class="btn-reset"
            @click="$emit('reset-simulation')"
            aria-label="Reset simulation"
          >
            Reset simulation
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import ReadinessSlider from '@components/ReadinessSlider/ReadinessSlider.vue';
import ReadinessCircle from '@components/ReadinessCircle/ReadinessCircle.vue';
import PayRangeHeader from '@components/PayRangeHeader/PayRangeHeader.vue';
import PositionIcon from '@components/svgs/PositionIcon.vue';
import { formatAssignmentFte } from '@components/EmployeeToPrimaryAssignmentBar/employeeToPrimaryAssignmentBarUtils';

const props = defineProps({
  calculator: {
    type: String,
    required: true,
    validator: (value) =>
      ['read-only', 'current-and-simulated', 'simulated-only', 'not-available'].includes(
        value
      )
  },
  asOfDate: {
    type: String,
    required: true
  },
  position: {
    type: Object,
    required: true
  },
  payRangeVersion: {
    type: Object,
    required: true
  },
  payRangeAdjusted: {
    type: Object,
    default: null
  },
  fteAdjustmentApplied: {
    type: Boolean,
    default: false
  },
  currentReadiness: {
    type: Number,
    default: null
  },
  fairAtCurrent: {
    type: Object,
    default: null
  },
  simulatedReadiness: {
    type: Number,
    default: null
  },
  fairAtSimulated: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:simulatedReadiness', 'reset-simulation', 'learn-more']);

const payRangeHeaderName = computed(() => {
  const category = props.payRangeVersion.jobCategory || '';
  const level = props.payRangeVersion.level || '';
  return category && level ? `${category} › ${level}` : category || level;
});

const positionFteLabel = computed(() => formatAssignmentFte(props.position.fte));

const adjustedLabel = computed(() => {
  if (!props.payRangeAdjusted) return '';
  const fte = props.position.fte || 0.5;
  return `Pay range adjusted for ${fte} FTE · min and max × ${fte}`;
});

const formatAnchor = (value) => {
  if (value === null || value === undefined) return '';
  if (value >= 1000) {
    return `${Math.round(value / 1000)}K`;
  }
  return String(value);
};

const formatFairPay = (fairPay) => {
  if (!fairPay) return '';
  const { amount, currency, basis } = fairPay;
  const formattedAmount = amount >= 1000 ? `${Math.round(amount / 1000)}K` : String(amount);
  const basisText = basis ? ` / ${basis}` : '';
  return `${currency} ${formattedAmount}${basisText}`;
};

const handleSliderUpdate = (value) => {
  emit('update:simulatedReadiness', value);
};
</script>

<style scoped>
.fair-pay-calculator-card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
}

/* Not Available Mode */
.not-available-alert {
  display: flex;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
}

.alert-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.alert-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.alert-heading {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.alert-description {
  color: #475467;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.alert-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-learn-more {
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  color: #344054;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.btn-learn-more:hover {
  background: #f9fafb;
}

/* Title Row */
.title-row {
  display: flex;
  gap: 5px;
  align-items: center;
}

.title-text {
  color: #344054;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.title-date {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

/* Position Summary */
.position-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px 13px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
}

.position-row {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.position-label {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.position-detail {
  display: flex;
  gap: 8px;
  align-items: center;
}

.position-icon {
  flex-shrink: 0;
}

.position-code {
  color: #344054;
  font-family: Inter;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
}

.position-name {
  color: #344054;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.position-fte {
  color: #667085;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
}

.pay-range-code {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

/* Pay Range Summary */
.pay-range-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px 13px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
}

.pay-range-summary.adjusted {
  gap: 5px;
}

.pay-range-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.pay-range-label {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.pay-range-label-adjusted {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.pay-range-meta {
  display: flex;
  gap: 8px;
  align-items: center;
}

.pay-range-version {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.pay-range-date {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.pay-range-detail {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.anchors-row {
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 0 28px;
}

.anchor-label {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.anchor-value {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

/* Current Readiness Row */
.current-readiness-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 23px;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
}

.readiness-info {
  display: flex;
  gap: 7px;
  align-items: center;
}

.readiness-label {
  color: #344054;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.readiness-value {
  color: #667085;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.fair-pay-info {
  display: flex;
  gap: 7px;
  align-items: center;
}

.fair-pay-label {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.fair-pay-amount {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.fair-pay-variance {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

/* Simulate Readiness Row */
.simulate-readiness-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 23px;
  row-gap: 8px;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
}

.simulate-label-wrapper {
  display: flex;
  gap: 7px;
  flex-shrink: 0;
  max-width: 7rem;
}

.simulate-label {
  color: #344054;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.slider-wrapper {
  min-width: 0;
  width: 100%;
  max-width: 381px;
  justify-self: stretch;
}

.slider-wrapper :deep(.readiness-slider) {
  max-width: 100%;
}

.simulate-info {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  max-width: 100%;
}

.btn-reset {
  padding: 0;
  background: none;
  border: none;
  color: #6941c6;
  font-family: Inter;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  text-decoration: underline;
  cursor: pointer;
}

.btn-reset:hover {
  color: #53389e;
}
</style>
